import React, {useRef, useState, useCallback, useEffect} from 'react';
import {
    mouseDown} from 'actions/index.js';
export const useParameterMouse = ({
    root,
    stepData,
    track,
    shouldRound,
    min,
    max,
    height,
    ref,
    setEffectParameterValue,
    setEffectParameterValues,
    parameter,
    selectedSteps,
    cirklonMode
}) => {

    const getId = () => {
        return parameter.id;
    };

    useEffect(() => {
        ref.current.addEventListener("touchmove", onMouseMove, {capture: true});
        ref.current.addEventListener("touchstart", onMouseDown, {capture: true});
        return () => {
            ref.current.removeEventListener("touchmove", onMouseMove); 
            ref.current.removeEventListener("touchstart", onMouseDown); 
        };
    }, [ref, track, parameter]);

    const onMouseUp = useCallback((e) => {
        track.sequencerEngine.dispatch(mouseDown(false));
    }, [track]);

    const onMouseDown = useCallback((e) => {
        e.stopPropagation();
        if (!ref.current) {
            return;
        }
        let newVolume = getVolumeFromEvent(e, getId(), min, max, height, root, ref.current);
        if (shouldRound) {
            newVolume = Math.round(newVolume);
        }
        parameter.set(newVolume, true);
        setEffectParameterValue(parameter.id, newVolume);
        track.sequencerEngine.dispatch(mouseDown(true));
    }, [parameter, cirklonMode, selectedSteps, track, stepData]);

    const onMouseMove = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!track.sequencerEngine.state.keyboard.isMouseDown || !ref.current) {
            return;
        }
        let newVolume = getVolumeFromEvent(e, getId(), min, max, height, root, ref.current);
        if (shouldRound) {
            newVolume = Math.round(newVolume);
        }
        if (cirklonMode === 'GANG' && selectedSteps.length > 0) {
            let bulk = {};
            for (let step of selectedSteps) {
                let _parameter = getParameter(step.parameters, parameter);
                let {id, value} = _parameter.set(newVolume, true, true);
                bulk[id] = value;
            }
            setEffectParameterValues(bulk);
        } else if (cirklonMode === 'SLOPE' && selectedSteps.length > 0) {
            let stepToStepNumber = {};
            let pattern = track.getEasyPattern();
            selectedSteps.forEach(
                stepData =>
                    stepToStepNumber[stepData.id] = pattern.findStepNumber(stepData));
            selectedSteps.sort((a,b) => stepToStepNumber[a.id] - stepToStepNumber[b.id]);
            let trackParameter = getParameter(track.trackData.parameters, parameter);
            let minParameter = getParameter(selectedSteps[0].parameters, parameter);
            let minParameterValue = minParameter.get() === null ?
                trackParameter.get() : minParameter.get();
            let maxParameter = getParameter(selectedSteps[selectedSteps.length - 1].parameters, parameter);
            let maxParameterValue = maxParameter.get() === null ?
                trackParameter.get() : maxParameter.get();

            let counter = 0.0;
            let bulk = {};

            let parameterIndex = selectedSteps.indexOf(stepData);

            let minDiff = newVolume - minParameterValue;
            let maxDiff = maxParameterValue - newVolume;

            for (let step of selectedSteps) {
                let _parameter = getParameter(step.parameters, parameter);
                if (counter < parameterIndex) {
                    let ratio = (counter / parameterIndex);
                    let _value = minParameterValue + ratio * minDiff;
                    let {id, value} = _parameter.set(_value, true, true);
                    bulk[id] = value;
                } else {
                    // 0 1 2** 3 4 5
                    // 3 - 2 / (5 -2) = 1/3
                    let ratio = (counter - parameterIndex) / (selectedSteps.length - 1 - parameterIndex)
                    if (isNaN(ratio)) {
                        ratio = 0;
                    }
                    let _value = newVolume + ratio * maxDiff;
                    let {id, value} = _parameter.set(_value, true, true);
                    bulk[id] = value;
                }
                counter++;
            }
            setEffectParameterValues(bulk);
        } else {
            parameter.set(newVolume, true);
            setEffectParameterValue(parameter.id, newVolume);
        }
    }, [parameter, cirklonMode, selectedSteps, track, stepData]);

    return {
        onMouseMove, onMouseDown
    };

};

export function getVolumeFromEvent(e, id, min, max, height, root, el) {
    let rect = el.getBoundingClientRect();
    let clientY = e.clientY;
    if (e.touches) {
        clientY = e.touches[0].clientY;
    }
    let y = height - (clientY - rect.top);
    let vol = getVolumeFromY(y, min, max, height, root);
    if (isNaN(vol)) {
        return -100;
    }
    return vol;
}

export function getHeight(volume, min=-40, max=4, height, root) {
    let ratio = getVolumeRatio(volume, min, max, height);
    return height * Math.pow(ratio, 1.0/root) + 4;
}

function getVolumeFromY(y, min=-40, max=4, height, root) {
    y = Math.min(Math.max(0, y), height);
    let ratio = y / height;
    let fullRatio = Math.pow(ratio, root);
    return min + (max - min) * fullRatio;
}

function getVolumeRatio(volume, min=-40, max=4) {
    return (volume - min) / (max - min);
}


const getParameter = (parameters, parameter) => {
    return parameters.find(x => x.name === parameter.name);
}
